import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { apiUrl } from "../../../constants";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";
import {
  Calendar,
  IndianRupee,
  TrendingUp,
  PieChart as PieChartIcon,
  ChevronDown,
  User,
  ChevronUp,
  BarChart3,
  Activity,
  Users,
  UserCheck,
  Package,
  CalendarDays,
  Filter,
  Download,
  Coins,
  TrendingDown,
  Gift,
  AlertTriangle,
} from "lucide-react";

const styles = {
  container: {
    background: "linear-gradient(135deg, #4f46e5, #3b82f6)",
    color: "white",
    padding: "2rem 2.5rem",
    borderRadius: "12px",
    boxShadow: "0 10px 15px rgba(59, 130, 246, 0.3)",
    maxWidth: "480px",
    fontFamily: "'Poppins', sans-serif",
  },
  title: {
    fontWeight: 700,
    fontSize: "2.8rem",
    margin: 0,
    textShadow: "1px 1px 3px rgba(0,0,0,0.3)",
  },
  subtitle: {
    marginTop: "0.75rem",
    fontWeight: 500,
    fontSize: "1.15rem",
    opacity: 0.85,
    textShadow: "1px 1px 2px rgba(0,0,0,0.15)",
  },
};

const COLORS = {
  primary: "#3B82F6",
  secondary: "#10B981",
  accent: "#F59E0B",
  danger: "#EF4444",
  purple: "#8B5CF6",
  pink: "#EC4899",
  indigo: "#6366F1",
  teal: "#14B8A6",
};

const PIE_COLORS = [
  COLORS.primary,
  COLORS.secondary,
  COLORS.accent,
  COLORS.danger,
  COLORS.purple,
  COLORS.pink,
];

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount);
};

// Date filtering utility functions
const getDateParts = (dateStr) => {
  const date = new Date(dateStr);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    date: date.getDate(),
    fullDate: date.toISOString().split("T")[0],
  };
};

const filterDataByDate = (data, filterType, filterValue) => {
  if (!filterValue || filterType === "all") return data;

  return data.filter((item) => {
    if (!item.date) return false;

    const itemDate = getDateParts(item.date);

    switch (filterType) {
      case "year":
        return itemDate.year === parseInt(filterValue);
      case "month":
        const [year, month] = filterValue.split("-");
        return (
          itemDate.year === parseInt(year) && itemDate.month === parseInt(month)
        );
      case "date":
        return itemDate.fullDate === filterValue;
      case "range":
        const [startDate, endDate] = filterValue.split("_");
        const itemDateObj = new Date(item.date);
        return (
          itemDateObj >= new Date(startDate) && itemDateObj <= new Date(endDate)
        );
      default:
        return true;
    }
  });
};

const getUniqueYears = (data) => {
  const years = data
    .filter((item) => item.date)
    .map((item) => getDateParts(item.date).year)
    .filter((year, index, arr) => arr.indexOf(year) === index)
    .sort((a, b) => b - a);
  return years;
};

const getUniqueMonths = (data, selectedYear = null) => {
  const months = data
    .filter((item) => {
      if (!item.date) return false;
      const itemDate = getDateParts(item.date);
      return selectedYear ? itemDate.year === parseInt(selectedYear) : true;
    })
    .map((item) => {
      const itemDate = getDateParts(item.date);
      return `${itemDate.year}-${itemDate.month.toString().padStart(2, "0")}`;
    })
    .filter((month, index, arr) => arr.indexOf(month) === index)
    .sort((a, b) => b.localeCompare(a));
  return months;
};

const formatMonthDisplay = (monthStr) => {
  const [year, month] = monthStr.split("-");
  const date = new Date(year, month - 1);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
};

const getTodaysData = (salesData) => {
  const today = new Date().toISOString().split("T")[0];

  const todaysSales = salesData.filter((item) => {
    if (!item.date) return false;
    const itemDate = new Date(item.date).toISOString().split("T")[0];
    return itemDate === today;
  });

  const todaysRevenue = todaysSales.reduce(
    (sum, item) => sum + (item.amount || 0),
    0,
  );
  const todaysOrders = todaysSales.length;

  return {
    revenue: todaysRevenue,
    orders: todaysOrders,
    avgOrderValue: todaysOrders > 0 ? todaysRevenue / todaysOrders : 0,
  };
};

const FilterTabs = ({ tabs, activeTab, onTabChange }) => (
  <div className="flex flex-wrap gap-2 mb-6">
    {tabs.map((tab) => (
      <button
        key={tab}
        onClick={() => onTabChange(tab)}
        className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
          activeTab === tab
            ? "bg-blue-600 text-white shadow-md"
            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}
      >
        {tab}
      </button>
    ))}
  </div>
);

const DateFilter = ({
  filterType,
  setFilterType,
  filterValue,
  setFilterValue,
  availableYears,
  availableMonths,
  onClearFilter,
}) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const handleRangeChange = () => {
    if (startDate && endDate) {
      setFilterValue(`${startDate}_${endDate}`);
    }
  };

  const clearFilter = () => {
    setFilterType("all");
    setFilterValue("");
    setStartDate("");
    setEndDate("");
    onClearFilter();
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-800">Date Filters</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Filter Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter By
          </label>
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setFilterValue("");
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Time</option>
            <option value="year">Year</option>
            <option value="month">Month</option>
            <option value="date">Specific Date</option>
            <option value="range">Date Range</option>
          </select>
        </div>

        {/* Year Filter */}
        {filterType === "year" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Year
            </label>
            <select
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Choose Year</option>
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Month Filter */}
        {filterType === "month" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Month
            </label>
            <select
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Choose Month</option>
              {availableMonths.map((month) => (
                <option key={month} value={month}>
                  {formatMonthDisplay(month)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Specific Date Filter */}
        {filterType === "date" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Date
            </label>
            <input
              type="date"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}

        {/* Date Range Filter */}
        {filterType === "range" && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (e.target.value && endDate) {
                    setFilterValue(`${e.target.value}_${endDate}`);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  if (startDate && e.target.value) {
                    setFilterValue(`${startDate}_${e.target.value}`);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </>
        )}
      </div>

      {/* Clear Filter Button */}
      {filterType !== "all" && (
        <div className="mt-4 flex justify-start">
          <button
            onClick={clearFilter}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Clear Filter
          </button>
        </div>
      )}

      {/* Active Filter Display */}
      {filterType !== "all" && filterValue && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm font-medium text-blue-800">
            Active Filter: {filterType === "year" && `Year ${filterValue}`}
            {filterType === "month" && formatMonthDisplay(filterValue)}
            {filterType === "date" &&
              `Date ${new Date(filterValue).toLocaleDateString()}`}
            {filterType === "range" &&
              `${new Date(startDate).toLocaleDateString()} - ${new Date(
                endDate,
              ).toLocaleDateString()}`}
          </p>
        </div>
      )}
    </div>
  );
};

// total sales

const SalesDashboard = ({ data = [] }) => {
  const [filterType, setFilterType] = useState("all");
  const [filterValue, setFilterValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const COLORS = {
    primary: "#3B82F6",
    secondary: "#10B981",
    accent: "#F59E0B",
    danger: "#EF4444",
    purple: "#8B5CF6",
    pink: "#EC4899",
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const getDateParts = (dateStr) => {
    if (!dateStr) return null;

    let date;

    date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      date = new Date(dateStr + "T00:00:00");
    }

    if (isNaN(date.getTime())) {
      const parts = dateStr.split(/[\/\-]/);
      if (parts.length === 3) {
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const year = parseInt(parts[2]);
        date = new Date(year, month, day);
      }
    }

    if (isNaN(date.getTime())) {
      console.warn(`Invalid date format: ${dateStr}`);
      return null;
    }

    const localDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000,
    );

    return {
      year: localDate.getFullYear(),
      month: localDate.getMonth() + 1,
      date: localDate.getDate(),
      fullDate: localDate.toISOString().split("T")[0],
    };
  };

  const filterDataByDate = (data, filterType, filterValue) => {
    if (!filterValue || filterType === "all") return data;
    return data.filter((item) => {
      if (!item.Date) return false;

      const itemDate = getDateParts(item.Date);
      if (!itemDate) return false;
      switch (filterType) {
        case "year":
          return itemDate.year === parseInt(filterValue);
        case "month":
          const [year, month] = filterValue.split("-");
          return (
            itemDate.year === parseInt(year) &&
            itemDate.month === parseInt(month)
          );
        case "date":
          return itemDate.fullDate === filterValue;
        case "range":
          const [startDate, endDate] = filterValue.split("_");
          return itemDate.fullDate >= startDate && itemDate.fullDate <= endDate;
        default:
          return true;
      }
    });
  };

  const getUniqueYears = (data) => {
    return data
      .filter((item) => item.Date)
      .map((item) => getDateParts(item.Date))
      .filter((dateParts) => dateParts !== null)
      .map((dateParts) => dateParts.year)
      .filter((year, index, arr) => arr.indexOf(year) === index)
      .sort((a, b) => b - a);
  };

  const getUniqueMonths = (data) => {
    return data
      .filter((item) => item.Date)
      .map((item) => getDateParts(item.Date))
      .filter((dateParts) => dateParts !== null)
      .map((dateParts) => {
        return `${dateParts.year}-${dateParts.month
          .toString()
          .padStart(2, "0")}`;
      })
      .filter((month, index, arr) => arr.indexOf(month) === index)
      .sort((a, b) => b.localeCompare(a));
  };

  const formatMonthDisplay = (monthStr) => {
    const [year, month] = monthStr.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
  };

  // Filtered data
  const filteredData = useMemo(() => {
    return filterDataByDate(data, filterType, filterValue);
  }, [data, filterType, filterValue]);

  // Statistics calculations
  const stats = useMemo(() => {
    const totalRevenue = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.total || 0),
      0,
    );
    const totalOrders = filteredData.length;
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const totalTax = filteredData.reduce(
      (sum, item) =>
        sum + parseFloat(item.cgst || 0) + parseFloat(item.sgst || 0),
      0,
    );

    return { totalRevenue, totalOrders, averageOrderValue, totalTax };
  }, [filteredData]);

  const getChartData = () => {
    const dateMap = {};
    filteredData.forEach((item) => {
      const dateParts = getDateParts(item.Date);
      if (!dateParts) return; // Skip invalid dates

      const date = item.Date;
      const formattedDate = new Date(
        dateParts.year,
        dateParts.month - 1,
        dateParts.date,
      ).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      });

      if (!dateMap[date]) {
        dateMap[date] = {
          date,
          formattedDate,
          revenue: 0,
          orders: 0,
          fullDate: new Date(
            dateParts.year,
            dateParts.month - 1,
            dateParts.date,
          ),
        };
      }
      dateMap[date].revenue += parseFloat(item.total || 0);
      dateMap[date].orders += 1;
    });

    return Object.values(dateMap)
      .filter((item) => !isNaN(item.fullDate.getTime())) // Filter out invalid dates
      .sort((a, b) => a.fullDate - b.fullDate)
      .map((item) => ({
        ...item,
        date: item.formattedDate,
      }));
  };

  const getPaymentMethodData = () => {
    const paymentStats = { online: 0, cash: 0 };
    filteredData.forEach((item) => {
      paymentStats[item.paymentType] += parseFloat(item.total || 0);
    });
    return [
      { name: "Online", value: paymentStats.online, color: COLORS.primary },
      { name: "Cash", value: paymentStats.cash, color: COLORS.secondary },
    ];
  };

  const clearFilter = () => {
    setFilterType("all");
    setFilterValue("");
    setStartDate("");
    setEndDate("");
  };

  const availableYears = getUniqueYears(data);
  const availableMonths = getUniqueMonths(data);

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm text-gray-600">
                {/* First one (orders) should show "Transaction Count", second one (revenue) should show "Transaction Amount" */}
                {entry.dataKey === "orders"
                  ? "Transaction Count"
                  : "Transaction Amount"}
                :
              </span>
              <span className="text-sm font-medium text-gray-900">
                {entry.dataKey === "orders"
                  ? `${entry.value} transactions`
                  : formatCurrency(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Sales Tracker
          </h1>
        </div>

        {/* Date Filter */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-800">
              Date Filters
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter By
              </label>
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setFilterValue("");
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Time</option>
                <option value="year">Year</option>
                <option value="month">Month</option>
                <option value="date">Specific Date</option>
                <option value="range">Date Range</option>
              </select>
            </div>

            {filterType === "year" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Year
                </label>
                <select
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose Year</option>
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {filterType === "month" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Month
                </label>
                <select
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose Month</option>
                  {availableMonths.map((month) => (
                    <option key={month} value={month}>
                      {formatMonthDisplay(month)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {filterType === "date" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Date
                </label>
                <input
                  type="date"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {filterType === "range" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      if (e.target.value && endDate) {
                        setFilterValue(`${e.target.value}_${endDate}`);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    min={startDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      if (startDate && e.target.value) {
                        setFilterValue(`${startDate}_${e.target.value}`);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}
          </div>

          {filterType !== "all" && (
            <div className="mt-4">
              <button
                onClick={clearFilter}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Clear Filter
              </button>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div
            className="bg-white rounded-xl shadow-lg p-6 border-l-4"
            style={{ borderLeftColor: COLORS.primary }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Total Revenue
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{ color: COLORS.primary }}
                >
                  {formatCurrency(stats.totalRevenue)}
                </p>
              </div>
              <div
                className="p-3 rounded-full"
                style={{ backgroundColor: `${COLORS.primary}20` }}
              >
                <IndianRupee
                  className="w-6 h-6"
                  style={{ color: COLORS.primary }}
                />
              </div>
            </div>
          </div>

          <div
            className="bg-white rounded-xl shadow-lg p-6 border-l-4"
            style={{ borderLeftColor: COLORS.secondary }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Total Orders
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{ color: COLORS.secondary }}
                >
                  {stats.totalOrders.toLocaleString()}
                </p>
              </div>
              <div
                className="p-3 rounded-full"
                style={{ backgroundColor: `${COLORS.secondary}20` }}
              >
                <BarChart3
                  className="w-6 h-6"
                  style={{ color: COLORS.secondary }}
                />
              </div>
            </div>
          </div>

          <div
            className="bg-white rounded-xl shadow-lg p-6 border-l-4"
            style={{ borderLeftColor: COLORS.accent }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Average Order Value
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{ color: COLORS.accent }}
                >
                  {formatCurrency(stats.averageOrderValue)}
                </p>
              </div>
              <div
                className="p-3 rounded-full"
                style={{ backgroundColor: `${COLORS.accent}20` }}
              >
                <TrendingUp
                  className="w-6 h-6"
                  style={{ color: COLORS.accent }}
                />
              </div>
            </div>
          </div>

          <div
            className="bg-white rounded-xl shadow-lg p-6 border-l-4"
            style={{ borderLeftColor: COLORS.purple }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Total Tax Collected
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{ color: COLORS.purple }}
                >
                  {formatCurrency(stats.totalTax)}
                </p>
              </div>
              <div
                className="p-3 rounded-full"
                style={{ backgroundColor: `${COLORS.purple}20` }}
              >
                <Coins className="w-6 h-6" style={{ color: COLORS.purple }} />
              </div>
            </div>
          </div>
        </div>
        {/* Enhanced Performance Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Enhanced Revenue Trend */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Daily Sales & Transaction Volume
            </h2>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={getChartData()}>
                <defs>
                  <linearGradient
                    id="revenueGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={COLORS.primary}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor={COLORS.primary}
                      stopOpacity={0.05}
                    />
                  </linearGradient>
                  <linearGradient
                    id="ordersGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor={COLORS.secondary}
                      stopOpacity={0.3}
                    />
                    <stop
                      offset="95%"
                      stopColor={COLORS.secondary}
                      stopOpacity={0.05}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0f0f0"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#666" }}
                />
                <YAxis
                  yAxisId="revenue"
                  orientation="left"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#666" }}
                  tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                />
                <YAxis
                  yAxisId="orders"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#666" }}
                />
                <Tooltip content={<CustomTooltip />} />

                <Area
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="revenue"
                  stroke={COLORS.primary}
                  strokeWidth={3}
                  fill="url(#revenueGradient)"
                  name="Revenue"
                  dot={{ fill: COLORS.primary, strokeWidth: 2, r: 4 }}
                  activeDot={{
                    r: 6,
                    stroke: COLORS.primary,
                    strokeWidth: 2,
                    fill: "white",
                  }}
                />

                <Line
                  yAxisId="orders"
                  type="monotone"
                  dataKey="orders"
                  stroke={COLORS.secondary}
                  strokeWidth={2}
                  name="Orders"
                  dot={{ fill: COLORS.secondary, strokeWidth: 2, r: 3 }}
                  activeDot={{
                    r: 5,
                    stroke: COLORS.secondary,
                    strokeWidth: 2,
                    fill: "white",
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Payment Methods */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Payment Methods
            </h2>
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={getPaymentMethodData()}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={40}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={false}
                >
                  {getPaymentMethodData().map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      stroke="white"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [formatCurrency(value), "Amount"]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* System Health */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                System Health
              </h3>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm text-green-600 font-medium">
                  Excellent
                </span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Order Success Rate
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {filteredData.length > 0 ? "100%" : "0%"}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: filteredData.length > 0 ? "100%" : "0%" }}
                ></div>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Payment Success Rate
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {(() => {
                    if (filteredData.length === 0) return "0%";
                    const successfulPayments = filteredData.filter((item) => {
                      if (
                        item.paymentType === "online" ||
                        item.paymentType === "RAZORPAY"
                      ) {
                        return item.OnlineAmount > 0;
                      } else if (item.paymentType === "cash") {
                        return item.CashAmount > 0;
                      }
                      return parseFloat(item.total) > 0;
                    }).length;
                    const rate = Math.round(
                      (successfulPayments / filteredData.length) * 100,
                    );
                    return `${rate}%`;
                  })()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: (() => {
                      if (filteredData.length === 0) return "0%";
                      const successfulPayments = filteredData.filter((item) => {
                        if (
                          item.paymentType === "online" ||
                          item.paymentType === "RAZORPAY"
                        ) {
                          return item.OnlineAmount > 0;
                        } else if (item.paymentType === "cash") {
                          return item.CashAmount > 0;
                        }
                        return parseFloat(item.total) > 0;
                      }).length;
                      const rate = Math.round(
                        (successfulPayments / filteredData.length) * 100,
                      );
                      return `${rate}%`;
                    })(),
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Performance Insights */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Performance Insights
            </h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Total Revenue
                  </p>
                  <p className="text-xs text-gray-600">
                    ₹{stats.totalRevenue?.toLocaleString() || 0}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Average Order Value
                  </p>
                  <p className="text-xs text-gray-600">
                    ₹{stats.averageOrderValue?.toLocaleString() || 0}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Total Tax Collected
                  </p>
                  <p className="text-xs text-gray-600">
                    ₹{stats.totalTax?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Stats
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Total Orders</span>
                <span className="text-sm font-medium text-gray-900">
                  {stats.totalOrders || 0}
                </span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Online vs Cash</span>
                <span className="text-sm font-medium text-gray-900">
                  {filteredData.length > 0
                    ? Math.round(
                        (filteredData.filter(
                          (item) => item.paymentType === "online",
                        ).length /
                          filteredData.length) *
                          100,
                      )
                    : 0}
                  % Online
                </span>
              </div>

              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Avg. Daily Orders</span>
                <span className="text-sm font-medium text-gray-900">
                  {stats.totalOrders ? Math.round(stats.totalOrders / 7) : 0}
                </span>
              </div>

              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-600">Active Records</span>
                <span className="text-sm font-medium text-gray-900">
                  {filteredData.length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Daily Performance Chart */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Daily Performance Overview
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={getChartData()} barGap={10}>
              <defs>
                <linearGradient id="barGradient1" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={COLORS.secondary}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={COLORS.secondary}
                    stopOpacity={0.6}
                  />
                </linearGradient>
                <linearGradient id="barGradient2" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={COLORS.primary}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={COLORS.primary}
                    stopOpacity={0.6}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f0f0f0"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#666" }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#666" }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="orders"
                fill="url(#barGradient1)"
                name="Orders"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="revenue"
                fill="url(#barGradient2)"
                name="Revenue"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const EmployeePerformanceCard = ({ employeeData }) => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [viewMode, setViewMode] = useState("overall");

  const getEmployeePerformance = () => {
    if (!Array.isArray(employeeData)) return [];

    return employeeData
      .map((employee) => {
        // Calculate total redemptions for the employee
        const totalRedemptions =
          employee.activities?.reduce(
            (sum, activity) => sum + (activity.count || 0),
            0,
          ) || 0;

        const activitiesByDate =
          employee.activities?.reduce((acc, activity) => {
            const date = activity.date;
            if (!acc[date]) {
              acc[date] = [];
            }
            acc[date].push(activity);
            return acc;
          }, {}) || {};

        // Calculate daily totals
        const dailyTotals = Object.entries(activitiesByDate)
          .map(([date, activities]) => ({
            date,
            totalCount: activities.reduce(
              (sum, act) => sum + (act.count || 0),
              0,
            ),
            activities: activities,
          }))
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        return {
          id: employee.empid || employee._id,
          empid: employee.empid,
          name: employee.name || "Unknown Employee",
          totalRedemptions,
          totalActivities: employee.activities?.length || 0,
          uniqueActivityNames: [
            ...new Set(employee.activities?.map((act) => act.name) || []),
          ],
          activities: employee.activities || [],
          dailyTotals,
        };
      })
      .sort((a, b) => b.totalRedemptions - a.totalRedemptions);
  };

  const employeePerformance = getEmployeePerformance();
  const totalEmployees = employeePerformance.length;
  const totalRedemptions = employeePerformance.reduce(
    (sum, emp) => sum + emp.totalRedemptions,
    0,
  );

  const handleEmployeeClick = (employee) => {
    if (expandedEmployee === employee.id) {
      setExpandedEmployee(null);
    } else {
      setExpandedEmployee(employee.id);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
      <div className="mb-8 ">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
          Employee Tracker
        </h2>
      </div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("overall")}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === "overall"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Overall
            </button>
            <button
              onClick={() => setViewMode("daywise")}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === "daywise"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Day-wise
            </button>
          </div>
          <div className="text-sm text-gray-600">
            {totalEmployees} Employees
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{totalEmployees}</p>
          <p className="text-sm text-gray-600">Total Employees</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-600">
            {totalRedemptions}
          </p>
          <p className="text-sm text-gray-600">Total Redemptions</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">
            {employeePerformance.reduce(
              (sum, emp) => sum + emp.totalActivities,
              0,
            )}
          </p>
          <p className="text-sm text-gray-600">Total Activities</p>
        </div>
      </div>

      {/* Employee List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {employeePerformance.length > 0 ? (
          employeePerformance.map((employee, index) => (
            <div
              key={employee.id}
              className="border border-gray-200 rounded-lg"
            >
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => handleEmployeeClick(employee)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {employee.name}
                    </h4>
                    <p className="text-xs text-gray-500">
                      ID: {employee.empid} • Rank #{index + 1}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="font-semibold text-blue-600">
                      {employee.totalRedemptions}
                    </p>
                    <p className="text-xs text-gray-500">redemptions</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-purple-600">
                      {employee.totalActivities}
                    </p>
                    <p className="text-xs text-gray-500">activities</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-orange-600">
                      {employee.uniqueActivityNames.length}
                    </p>
                    <p className="text-xs text-gray-500">unique types</p>
                  </div>
                  {expandedEmployee === employee.id ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>

              {expandedEmployee === employee.id && (
                <div className="border-t border-gray-200 bg-gray-50">
                  <div className="p-4">
                    {viewMode === "overall" ? (
                      // Overall Activity View
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                          <Activity className="w-4 h-4" />
                          Activity Breakdown ({employee.totalActivities}{" "}
                          activities)
                        </h5>

                        {employee.activities.length > 0 ? (
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {employee.activities.map((activity, actIndex) => (
                              <div
                                key={`${activity._id || actIndex}-${actIndex}`}
                                className="flex items-center justify-between py-2 px-3 bg-white rounded border"
                              >
                                <div className="flex-1">
                                  <span className="text-sm text-gray-700 font-medium block">
                                    {activity.name || "Unknown Activity"}
                                  </span>
                                  {activity.date && (
                                    <span className="text-xs text-gray-500">
                                      {new Date(
                                        activity.date,
                                      ).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                                <span className="text-sm font-semibold text-green-600 ml-2">
                                  {activity.count || 0} redemptions
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">
                            No activities assigned
                          </p>
                        )}
                      </div>
                    ) : (
                      // Day-wise Activity View
                      <div>
                        <h5 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                          <CalendarDays className="w-4 h-4" />
                          Day-wise Performance ({
                            employee.dailyTotals.length
                          }{" "}
                          days)
                        </h5>

                        {employee.dailyTotals.length > 0 ? (
                          <div className="space-y-3 max-h-48 overflow-y-auto">
                            {employee.dailyTotals.map((dayData, dayIndex) => (
                              <div
                                key={`${dayData.date}-${dayIndex}`}
                                className="bg-white rounded border p-3"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-sm font-medium text-gray-700">
                                    {new Date(dayData.date).toLocaleDateString(
                                      "en-US",
                                      {
                                        weekday: "short",
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      },
                                    )}
                                  </span>
                                  <span className="text-sm font-semibold text-blue-600">
                                    {dayData.totalCount} total redemptions
                                  </span>
                                </div>

                                {/* Activities for this day */}
                                <div className="space-y-1">
                                  {dayData.activities.map(
                                    (activity, actIndex) => (
                                      <div
                                        key={`${
                                          activity._id || actIndex
                                        }-day-${actIndex}`}
                                        className="flex items-center justify-between py-1 px-2 bg-gray-50 rounded text-xs"
                                      >
                                        <span className="text-gray-600">
                                          {activity.name || "Unknown Activity"}
                                        </span>
                                        <span className="font-medium text-green-600">
                                          {activity.count || 0}
                                        </span>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 italic">
                            No daily data available
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-2">
              <Users className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-gray-500">No employee data available</p>
            <p className="text-xs text-gray-400 mt-1">
              Check if employee data is being fetched correctly
            </p>
          </div>
        )}
      </div>

      {/* Top Performer Highlight */}
      {employeePerformance.length > 0 &&
        employeePerformance[0].totalRedemptions > 0 && (
          <div className="mt-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">👑</span>
              </div>
              <span className="text-sm font-medium text-yellow-800">
                Top Performer
              </span>
            </div>
            <p className="text-sm text-gray-700">
              <span className="font-semibold">
                {employeePerformance[0].name}
              </span>{" "}
              leads with{" "}
              <span className="font-semibold text-yellow-700">
                {employeePerformance[0].totalRedemptions} redemptions
              </span>{" "}
              across {employeePerformance[0].totalActivities} activities
            </p>
          </div>
        )}

      {/* Performance Distribution Chart */}
      {employeePerformance.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Performance Distribution
          </h4>
          <div className="space-y-2">
            {employeePerformance.slice(0, 5).map((employee, index) => {
              const maxRedemptions = employeePerformance[0].totalRedemptions;
              const percentage =
                maxRedemptions > 0
                  ? (employee.totalRedemptions / maxRedemptions) * 100
                  : 0;

              return (
                <div key={employee.id} className="flex items-center gap-3">
                  <div className="w-20 text-xs text-gray-600 truncate">
                    {employee.name}
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="w-12 text-xs text-gray-600 text-right">
                    {employee.totalRedemptions}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// for present day data
const TodaysStatsCard = ({ salesData }) => {
  const todaysData = getTodaysData(salesData);

  return (
    <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg p-6 text-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Today's Performance</h3>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold">
            {formatCurrency(todaysData.revenue)}
          </p>
          <p className="text-sm opacity-80">Revenue</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{todaysData.orders}</p>
          <p className="text-sm opacity-80">Orders</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">
            {formatCurrency(todaysData.avgOrderValue)}
          </p>
          <p className="text-sm opacity-80">Avg Order</p>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-white/20">
        <p className="text-sm opacity-80">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>
      </div>
    </div>
  );
};

const AdminCoinAnalytics = ({ priceData }) => {
  const transactionData = priceData;

  const [dateFilter, setDateFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");

  // Filter data based on date selection
  const filteredData = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return transactionData.filter((transaction) => {
      const transactionDate = new Date(transaction.createdAt);

      switch (dateFilter) {
        case "today":
          return transactionDate >= today;
        case "week":
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return transactionDate >= weekAgo;
        case "month":
          const monthAgo = new Date(today);
          monthAgo.setMonth(monthAgo.getMonth() - 1);
          return transactionDate >= monthAgo;
        case "specific":
          if (!selectedDate) return true;
          const selected = new Date(selectedDate);
          return transactionDate.toDateString() === selected.toDateString();
        default:
          return true;
      }
    });
  }, [dateFilter, selectedDate]);

  // Calculate statistics
  const stats = useMemo(() => {
    const credits = filteredData.filter((t) => t.type === "credit");
    const debits = filteredData.filter((t) => t.type === "debit");

    // All coins from credits (purchased + dispute + package + complementary)
    const allCoins = credits.filter((t) => t.coins > 0);
    const totalRevenue = allCoins.reduce((sum, t) => sum + t.coins, 0);

    // Separate purchased coins for transaction count
    const purchasedCoins = credits.filter(
      (t) =>
        t.description.toLowerCase().includes("purchased") &&
        !t.description.toLowerCase().includes("dispute") &&
        !t.description.toLowerCase().includes("package") &&
        !t.description.toLowerCase().includes("complementary") &&
        !t.description.toLowerCase().includes("unlimited") &&
        t.coins > 0,
    );
    const avgRevenue = allCoins.length > 0 ? totalRevenue / allCoins.length : 0;

    // Complementary coins distributed - DISPUTE coins
    const disputeCoins = credits
      .filter(
        (t) =>
          t.description.toLowerCase().includes("dispute") ||
          t.description.toLowerCase().includes("dispute (complementary coins)"),
      )
      .reduce((sum, t) => sum + t.coins, 0);

    // Complementary coins distributed - PACKAGE coins
    const packageCoins = credits
      .filter((t) =>
        t.description.toLowerCase().includes("package (complementary coins)"),
      )
      .reduce((sum, t) => sum + t.coins, 0);

    const totalComplementary = disputeCoins + packageCoins;

    // System usage
    const totalRedeemed = debits.reduce((sum, t) => sum + t.coins, 0);

    // Activity analytics
    const paidActivities = debits.filter(
      (t) => t.activity && t.coins > 0,
    ).length;
    const freeActivities = debits.filter(
      (t) => t.description.toLowerCase().includes("unlimited") || t.coins === 0,
    ).length;

    // System totals
    const totalIssued = credits.reduce((sum, t) => sum + t.coins, 0);

    // System balance
    const systemBalance = totalIssued - totalRedeemed;

    return {
      totalRevenue,
      avgRevenue,
      totalRedeemed,
      totalComplementary,
      disputeCoins,
      packageCoins,
      paidActivities,
      freeActivities,
      totalIssued,
      systemBalance,
      revenueTransactions: purchasedCoins.length,
      totalRedemptions: debits.length,
    };
  }, [filteredData]);

  const StatCard = ({ title, value, icon: Icon, gradient, subtitle }) => (
    <div
      className={`bg-gradient-to-br ${gradient} text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/80 text-sm font-medium mb-1">{title}</p>
          <p className="text-3xl font-bold">{value}</p>
          {subtitle && <p className="text-white/70 text-xs mt-1">{subtitle}</p>}
        </div>
        <Icon className="h-10 w-10 text-white/80" />
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="mb-8 ">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
          Coins Tracker
        </h2>
      </div>
      {/* Date Filter Controls */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 mb-8 shadow-lg border border-white/20">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <span className="font-semibold text-gray-700">Select Period:</span>
          </div>

          <div className="flex gap-2 flex-wrap">
            {[
              { value: "all", label: "All Time" },
              { value: "today", label: "Today" },
              { value: "week", label: "This Week" },
              { value: "month", label: "This Month" },
              { value: "specific", label: "Custom Date" },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setDateFilter(option.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  dateFilter === option.value
                    ? "bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {dateFilter === "specific" && (
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          )}
        </div>
      </div>

      {/* Single Comprehensive Analytics Card */}
      <div className="bg-white/90 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/30">
        {/* Top Metrics Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-emerald-500 to-green-500 text-white rounded-xl p-4 text-center">
            <Coins className="h-8 w-8 mx-auto mb-2 opacity-80" />
            <p className="text-2xl font-bold">{stats.totalRevenue}</p>
            <p className="text-xs opacity-80">Total Revenue Coins</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white rounded-xl p-4 text-center">
            <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-80" />
            <p className="text-2xl font-bold">{Math.round(stats.avgRevenue)}</p>
            <p className="text-xs opacity-80">Avg Coins</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-xl p-4 text-center">
            <TrendingDown className="h-8 w-8 mx-auto mb-2 opacity-80" />
            <p className="text-2xl font-bold">{stats.totalRedeemed}</p>
            <p className="text-xs opacity-80">Coins Redeemed</p>
          </div>
          <div
            className={`bg-gradient-to-br ${
              stats.systemBalance >= 0
                ? "from-green-500 to-emerald-500"
                : "from-red-500 to-pink-500"
            } text-white rounded-xl p-4 text-center`}
          >
            <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-80" />
            <p className="text-2xl font-bold">{stats.systemBalance}</p>
            <p className="text-xs opacity-80">Overall-Used Coins</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Complementary Distribution */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
              <Gift className="h-5 w-5 text-purple-600" />
              Complementary Coins Distribution
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-200">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <div>
                    <span className="font-medium text-gray-800 text-sm">
                      Dispute Coins Credits
                    </span>
                    <p className="text-xs text-gray-600">Dispute Service</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-orange-600">
                  {stats.disputeCoins}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <Gift className="h-4 w-4 text-green-600" />
                  <div>
                    <span className="font-medium text-gray-800 text-sm">
                      Package Coin Credit
                    </span>
                    <p className="text-xs text-gray-600">Package Service</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-green-600">
                  {stats.packageCoins}
                </span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-gray-900">
                    Total Complementary Distributed
                  </span>
                  <span className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {stats.totalComplementary}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Engagement */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
              <Activity className="h-5 w-5 text-blue-600" />
              Packages Engagement
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                <div>
                  <span className="font-medium text-gray-800 text-sm">
                    Coins Packages
                  </span>
                  <p className="text-xs text-gray-600">Regular packages</p>
                </div>
                <span className="text-lg font-bold text-blue-600">
                  {stats.paidActivities}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                <div>
                  <span className="font-medium text-gray-800 text-sm">
                    Unlimited Packages
                  </span>
                  <p className="text-xs text-gray-600">Unlimited </p>
                </div>
                <span className="text-lg font-bold text-purple-600">
                  {stats.freeActivities}
                </span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-gray-900">
                    Total Packages
                  </span>
                  <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                    {stats.paidActivities + stats.freeActivities}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Summary Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-gray-200">
          <div className="text-center p-4 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl text-white">
            <p className="text-2xl font-bold">{stats.totalIssued}</p>
            <p className="text-sm opacity-80">Total Coins Issued</p>
            <p className="text-xs opacity-70">Revenue + complementary</p>
          </div>

          <div className="text-center p-4 bg-gradient-to-br from-blue-500 to-teal-500 rounded-xl text-white">
            <p className="text-2xl font-bold">
              {stats.totalIssued > 0
                ? Math.round((stats.totalRedeemed / stats.totalIssued) * 100)
                : 0}
              %
            </p>
            <p className="text-sm opacity-80">System Utilization</p>
            <p className="text-xs opacity-70">Coins actively used</p>
          </div>

          <div className="text-center p-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl text-white">
            <p className="text-2xl font-bold">{filteredData.length}</p>
            <p className="text-sm opacity-80">Transaction Volume</p>
            <p className="text-xs opacity-70">System transactions</p>
          </div>
        </div>
      </div>
    </div>
  );
};
const ManualSalesEntryCard = ({ salesData }) => {
  const [dateFilter, setDateFilter] = useState("today");
  const [customDate, setCustomDate] = useState("");

  const filterDataByDate = (data, filter, customDate) => {
    if (!data || !Array.isArray(data)) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return data.filter((item) => {
      const itemDate = new Date(item.date);
      itemDate.setHours(0, 0, 0, 0);

      switch (filter) {
        case "today":
          return itemDate.getTime() === today.getTime();
        case "yesterday":
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return itemDate.getTime() === yesterday.getTime();
        case "thisWeek":
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          return itemDate >= weekStart && itemDate <= today;
        case "thisMonth":
          const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
          return itemDate >= monthStart && itemDate <= today;
        case "custom":
          if (!customDate) return true;
          const selectedDate = new Date(customDate);
          selectedDate.setHours(0, 0, 0, 0);
          return itemDate.getTime() === selectedDate.getTime();
        default:
          return true;
      }
    });
  };

  // filter based on data availability
  const getInitialFilter = () => {
    if (!salesData || !Array.isArray(salesData)) return "all";

    const todayData = filterDataByDate(salesData, "today", "");
    return todayData.length > 0 ? "today" : "all";
  };

  // filter on component mount
  React.useEffect(() => {
    setDateFilter(getInitialFilter());
  }, [salesData]);

  const filteredData = useMemo(() => {
    return filterDataByDate(salesData, dateFilter, customDate);
  }, [salesData, dateFilter, customDate]);

  const calculateStats = (data) => {
    if (!data || !Array.isArray(data)) {
      return {
        totalCash: 0,
        totalOnline: 0,
        totalCreditCard: 0,
        totalAmount: 0,
        totalTransactions: 0,
      };
    }

    return data.reduce(
      (acc, item) => {
        const cash = parseFloat(item.cashAmount) || 0;
        const online = parseFloat(item.onlineAmount) || 0;
        const creditCard = parseFloat(item.creditCardAmount) || 0;
        const total = parseFloat(item.totalAmount) || 0;
        const transactions = parseInt(item.totalTransactions) || 0;

        return {
          totalCash: acc.totalCash + cash,
          totalOnline: acc.totalOnline + online,
          totalCreditCard: acc.totalCreditCard + creditCard,
          totalAmount: acc.totalAmount + total,
          totalTransactions: acc.totalTransactions + transactions,
        };
      },
      {
        totalCash: 0,
        totalOnline: 0,
        totalCreditCard: 0,
        totalAmount: 0,
        totalTransactions: 0,
      },
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const stats = calculateStats(filteredData);

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "Today's";
      case "yesterday":
        return "Yesterday's";
      case "thisWeek":
        return "This Week's";
      case "thisMonth":
        return "This Month's";
      case "custom":
        return customDate
          ? `${new Date(customDate).toLocaleDateString()}'s`
          : "Custom Date";
      default:
        return "All Time";
    }
  };

  return (
    <div className="bg-gradient-to-r from-sky-400 to-sky-600 rounded-xl shadow-lg p-6 text-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Manual Sales Entry</h3>
        <div className="flex gap-2 items-center">
          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              if (e.target.value !== "custom") {
                setCustomDate("");
              }
            }}
            className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-md px-2 py-1 text-sm text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            <option value="all" className="text-gray-800">
              All Time
            </option>
            <option value="today" className="text-gray-800">
              Today
            </option>
            <option value="yesterday" className="text-gray-800">
              Yesterday
            </option>
            <option value="thisWeek" className="text-gray-800">
              This Week
            </option>
            <option value="thisMonth" className="text-gray-800">
              This Month
            </option>
            <option value="custom" className="text-gray-800">
              Custom Date
            </option>
          </select>
          {dateFilter === "custom" && (
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-md px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white/50"
            />
          )}
        </div>
      </div>

      {/* Main Total Amount */}
      <div className="text-center mb-6">
        <p className="text-3xl font-bold mb-2">
          {formatCurrency(stats.totalAmount)}
        </p>
        <p className="text-sm opacity-80">{getFilterLabel()} Total Sales</p>
      </div>

      {/* Payment Method Breakdown */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center bg-gradient-to-br from-pink-400/20 to-rose-400/20 backdrop-blur-sm border border-white/20 rounded-lg p-3">
          <p className="text-xl font-bold">{formatCurrency(stats.totalCash)}</p>
          <p className="text-sm opacity-80">Cash</p>
        </div>
        <div className="text-center bg-gradient-to-br from-cyan-400/20 to-blue-400/20 backdrop-blur-sm border border-white/20 rounded-lg p-3">
          <p className="text-xl font-bold">
            {formatCurrency(stats.totalOnline)}
          </p>
          <p className="text-sm opacity-80">Online</p>
        </div>
        <div className="text-center bg-gradient-to-br from-amber-400/20 to-orange-400/20 backdrop-blur-sm border border-white/20 rounded-lg p-3">
          <p className="text-xl font-bold">
            {formatCurrency(stats.totalCreditCard)}
          </p>
          <p className="text-sm opacity-80">Credit Card</p>
        </div>
      </div>

      {/* Additional Stats */}
      <div className="mt-4 pt-4 border-t border-white/20">
        <div className="flex justify-between items-center">
          <p className="text-sm opacity-80">
            {getFilterLabel()} data • {stats.totalTransactions} transaction
            {stats.totalTransactions !== 1 ? "s" : ""}
          </p>
          <p className="text-sm opacity-80">
            {filteredData.length} entr{filteredData.length !== 1 ? "ies" : "y"}
          </p>
        </div>
      </div>
    </div>
  );
};
// for all taxes and discount
const DiscountTaxStatsCard = ({ transactionData }) => {
  const [dateFilter, setDateFilter] = useState("all");
  const [customDate, setCustomDate] = useState("");

  const filterDataByDate = (data, filter, customDate) => {
    if (!data || !Array.isArray(data)) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return data.filter((item) => {
      const itemDate = new Date(item.Date);
      itemDate.setHours(0, 0, 0, 0);

      switch (filter) {
        case "today":
          return itemDate.getTime() === today.getTime();
        case "yesterday":
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return itemDate.getTime() === yesterday.getTime();
        case "thisWeek":
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay());
          return itemDate >= weekStart && itemDate <= today;
        case "thisMonth":
          const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
          return itemDate >= monthStart && itemDate <= today;
        case "custom":
          if (!customDate) return true;
          const selectedDate = new Date(customDate);
          selectedDate.setHours(0, 0, 0, 0);
          return itemDate.getTime() === selectedDate.getTime();
        default:
          return true;
      }
    });
  };

  const filteredData = useMemo(() => {
    return filterDataByDate(transactionData, dateFilter, customDate);
  }, [transactionData, dateFilter, customDate]);

  const calculateStats = (data) => {
    if (!data || !Array.isArray(data)) {
      return {
        totalDiscount: 0,
        totalTax: 0,
        totalCGST: 0,
        totalSGST: 0,
        orders: 0,
      };
    }

    return data.reduce(
      (acc, item) => {
        const discount = parseFloat(item.discount) || 0;
        const cgst = parseFloat(item.cgst) || 0;
        const sgst = parseFloat(item.sgst) || 0;

        return {
          totalDiscount: acc.totalDiscount + discount,
          totalCGST: acc.totalCGST + cgst,
          totalSGST: acc.totalSGST + sgst,
          totalTax: acc.totalTax + cgst + sgst,
          orders: acc.orders + 1,
        };
      },
      {
        totalDiscount: 0,
        totalCGST: 0,
        totalSGST: 0,
        totalTax: 0,
        orders: 0,
      },
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const stats = calculateStats(filteredData);

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "Today's";
      case "yesterday":
        return "Yesterday's";
      case "thisWeek":
        return "This Week's";
      case "thisMonth":
        return "This Month's";
      case "custom":
        return customDate
          ? `${new Date(customDate).toLocaleDateString()}'s`
          : "Custom Date";
      default:
        return "All Time";
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Discount & Tax Summary</h3>
        <div className="flex gap-2 items-center">
          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              if (e.target.value !== "custom") {
                setCustomDate("");
              }
            }}
            className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-md px-2 py-1 text-sm text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            <option value="all" className="text-gray-800">
              All Time
            </option>
            <option value="today" className="text-gray-800">
              Today
            </option>
            <option value="yesterday" className="text-gray-800">
              Yesterday
            </option>
            <option value="thisWeek" className="text-gray-800">
              This Week
            </option>
            <option value="thisMonth" className="text-gray-800">
              This Month
            </option>
            <option value="custom" className="text-gray-800">
              Custom Date
            </option>
          </select>
          {dateFilter === "custom" && (
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-md px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white/50"
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <p className="text-2xl font-bold">
            {formatCurrency(stats.totalDiscount)}
          </p>
          <p className="text-sm opacity-80">{getFilterLabel()} Discount</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{formatCurrency(stats.totalTax)}</p>
          <p className="text-sm opacity-80">{getFilterLabel()} Tax</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="text-center">
          <p className="text-xl font-bold">{formatCurrency(stats.totalCGST)}</p>
          <p className="text-sm opacity-80">CGST</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-bold">{formatCurrency(stats.totalSGST)}</p>
          <p className="text-sm opacity-80">SGST</p>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-white/20">
        <p className="text-sm opacity-80">
          {getFilterLabel()} data • {stats.orders} order
          {stats.orders !== 1 ? "s" : ""}
        </p>
      </div>
    </div>
  );
};

const PaymentAnalyticsCard = ({ salesData }) => {
  const [dateFilter, setDateFilter] = useState("all");
  const [customDate, setCustomDate] = useState("");

  const normalizeDate = (dateString) => {
    if (!dateString) return null;

    let date;
    if (dateString.includes("/")) {
      const parts = dateString.split("/");
      if (parts.length === 3) {
        const month = parts[0].padStart(2, "0");
        const day = parts[1].padStart(2, "0");
        const year = parts[2];
        date = new Date(`${year}-${month}-${day}`);
      }
    } else {
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) return null;

    return date.toISOString().split("T")[0];
  };

  const today = new Date().toISOString().split("T")[0];

  const extractPaymentTypes = (paymentTypeString) => {
    if (!paymentTypeString) return [];

    const types = paymentTypeString
      .trim()
      .split(/\s+/)
      .filter((type) => type);
    return types.map((type) => type.toLowerCase());
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  // Filter data based on selected date filter
  const filterDataByDate = (data, filter, customDateValue) => {
    if (!data || !Array.isArray(data)) return [];

    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    return data.filter((item) => {
      if (!item.Date) return false;
      const itemDate = new Date(normalizeDate(item.Date));
      itemDate.setHours(0, 0, 0, 0);

      switch (filter) {
        case "today":
          return itemDate.getTime() === todayDate.getTime();
        case "yesterday":
          const yesterday = new Date(todayDate);
          yesterday.setDate(yesterday.getDate() - 1);
          return itemDate.getTime() === yesterday.getTime();
        case "thisWeek":
          const weekStart = new Date(todayDate);
          weekStart.setDate(todayDate.getDate() - todayDate.getDay());
          return itemDate >= weekStart && itemDate <= todayDate;
        case "thisMonth":
          const monthStart = new Date(
            todayDate.getFullYear(),
            todayDate.getMonth(),
            1,
          );
          return itemDate >= monthStart && itemDate <= todayDate;
        case "custom":
          if (!customDateValue) return true;
          const selectedDate = new Date(customDateValue);
          selectedDate.setHours(0, 0, 0, 0);
          return itemDate.getTime() === selectedDate.getTime();
        default:
          return true;
      }
    });
  };

  // Calculate today's payment breakdown (always shows today's data)
  const getTodaysPaymentBreakdown = () => {
    const todaysSales = salesData.filter((item) => {
      if (!item.Date) return false;
      const normalizedDate = normalizeDate(item.Date);
      return normalizedDate === today;
    });

    const breakdown = {
      cash: 0,
      card: 0,
      online: 0,
    };

    todaysSales.forEach((item) => {
      const cashAmount = parseFloat(item.CashAmount) || 0;
      const onlineAmount = parseFloat(item.OnlineAmount) || 0;
      const totalAmount = parseFloat(item.total) || 0;
      const paymentTypeStr = item.paymentType?.trim().toLowerCase() || "";

      if (cashAmount === 0 && onlineAmount === 0) {
        if (paymentTypeStr.includes("cash")) {
          breakdown.cash += totalAmount;
        } else if (paymentTypeStr.includes("card")) {
          breakdown.card += totalAmount;
        } else if (
          paymentTypeStr.includes("online") ||
          paymentTypeStr.includes("razorpay") ||
          paymentTypeStr.includes("upi")
        ) {
          breakdown.online += totalAmount;
        } else {
          breakdown.cash += totalAmount;
        }
      } else {
        if (paymentTypeStr === "cash online") {
          breakdown.cash += cashAmount;
          breakdown.online += onlineAmount;
        } else {
          const paymentTypes = extractPaymentTypes(item.paymentType);
          paymentTypes.forEach((type) => {
            if (type === "cash") breakdown.cash += cashAmount || totalAmount;
            else if (type === "card") breakdown.card += totalAmount;
            else if (type === "online" || type === "razorpay" || type === "upi")
              breakdown.online += onlineAmount || totalAmount;
          });
        }
      }
    });

    return breakdown;
  };

  // Calculate overall payment breakdown based on filter
  const getOverallPaymentBreakdown = () => {
    const filteredData = filterDataByDate(salesData, dateFilter, customDate);

    const breakdown = {
      cash: 0,
      card: 0,
      online: 0,
    };

    filteredData.forEach((item) => {
      const cashAmount = parseFloat(item.CashAmount) || 0;
      const onlineAmount = parseFloat(item.OnlineAmount) || 0;
      const totalAmount = parseFloat(item.total) || 0;
      const paymentTypeStr = item.paymentType?.trim().toLowerCase() || "";

      if (cashAmount === 0 && onlineAmount === 0) {
        if (paymentTypeStr.includes("cash")) {
          breakdown.cash += totalAmount;
        } else if (paymentTypeStr.includes("card")) {
          breakdown.card += totalAmount;
        } else if (
          paymentTypeStr.includes("online") ||
          paymentTypeStr.includes("razorpay") ||
          paymentTypeStr.includes("upi")
        ) {
          breakdown.online += totalAmount;
        } else {
          breakdown.cash += totalAmount;
        }
      } else {
        if (paymentTypeStr === "cash online") {
          breakdown.cash += cashAmount;
          breakdown.online += onlineAmount;
        } else {
          const paymentTypes = extractPaymentTypes(item.paymentType);
          paymentTypes.forEach((type) => {
            if (type === "cash") breakdown.cash += cashAmount || totalAmount;
            else if (type === "card") breakdown.card += totalAmount;
            else if (type === "online" || type === "razorpay" || type === "upi")
              breakdown.online += onlineAmount || totalAmount;
          });
        }
      }
    });

    return breakdown;
  };

  const todaysBreakdown = getTodaysPaymentBreakdown();
  const overallBreakdown = getOverallPaymentBreakdown();

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "Today's";
      case "yesterday":
        return "Yesterday's";
      case "thisWeek":
        return "This Week's";
      case "thisMonth":
        return "This Month's";
      case "custom":
        return customDate
          ? `${new Date(customDate).toLocaleDateString()}'s`
          : "Custom Date";
      default:
        return "All Time";
    }
  };

  return (
    <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Payment Analytics</h3>
        <div className="flex gap-2 items-center">
          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              if (e.target.value !== "custom") {
                setCustomDate("");
              }
            }}
            className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-md px-2 py-1 text-sm text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            <option value="all" className="text-gray-800">
              All Time
            </option>
            <option value="today" className="text-gray-800">
              Today
            </option>
            <option value="yesterday" className="text-gray-800">
              Yesterday
            </option>
            <option value="thisWeek" className="text-gray-800">
              This Week
            </option>
            <option value="thisMonth" className="text-gray-800">
              This Month
            </option>
            <option value="custom" className="text-gray-800">
              Custom Date
            </option>
          </select>
          {dateFilter === "custom" && (
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-md px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white/50"
            />
          )}
        </div>
      </div>

      {/* Today's Breakdown - Always shows today's data */}
      <div className="mb-6">
        <h4 className="text-sm font-medium mb-3 opacity-90">Today's Revenue</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center bg-white/10 rounded-lg p-3">
            <p className="text-xl font-bold">
              {formatCurrency(todaysBreakdown.cash)}
            </p>
            <p className="text-xs opacity-80">Cash</p>
          </div>
          <div className="text-center bg-white/10 rounded-lg p-3">
            <p className="text-xl font-bold">
              {formatCurrency(todaysBreakdown.card)}
            </p>
            <p className="text-xs opacity-80">Card</p>
          </div>
          <div className="text-center bg-white/10 rounded-lg p-3">
            <p className="text-xl font-bold">
              {formatCurrency(todaysBreakdown.online)}
            </p>
            <p className="text-xs opacity-80">Online</p>
          </div>
        </div>
      </div>

      {/* Overall Breakdown - Shows filtered data */}
      <div className="border-t border-white/20 pt-4">
        <h4 className="text-sm font-medium mb-3 opacity-90">
          {getFilterLabel()} Revenue
        </h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <p className="text-lg font-bold">
              {formatCurrency(overallBreakdown.cash)}
            </p>
            <p className="text-xs opacity-80">Cash</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold">
              {formatCurrency(overallBreakdown.card)}
            </p>
            <p className="text-xs opacity-80">Card</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-bold">
              {formatCurrency(overallBreakdown.online)}
            </p>
            <p className="text-xs opacity-80">Online</p>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-white/20">
        <p className="text-sm opacity-80">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </p>
      </div>
    </div>
  );
};

const ChartCard = ({ title, children, className = "" }) => (
  <div className={`bg-white rounded-xl shadow-lg p-6 ${className}`}>
    <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
    {children}
  </div>
);
const ActivityAnalyticsCard = ({ activitiesData }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [customDate, setCustomDate] = useState("");

  const filterByDate = (data, filter, custom) => {
    if (!data || !Array.isArray(data)) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return data.filter((item) => {
      if (!item.date) return true;
      const itemDate = new Date(item.date);
      itemDate.setHours(0, 0, 0, 0);
      switch (filter) {
        case "today":
          return itemDate.getTime() === today.getTime();
        case "yesterday":
          const yest = new Date(today);
          yest.setDate(yest.getDate() - 1);
          return itemDate.getTime() === yest.getTime();
        case "thisWeek":
          const wStart = new Date(today);
          wStart.setDate(today.getDate() - today.getDay());
          return itemDate >= wStart && itemDate <= today;
        case "thisMonth":
          const mStart = new Date(today.getFullYear(), today.getMonth(), 1);
          return itemDate >= mStart && itemDate <= today;
        case "custom":
          if (!custom) return true;
          const sel = new Date(custom);
          sel.setHours(0, 0, 0, 0);
          return itemDate.getTime() === sel.getTime();
        default:
          return true;
      }
    });
  };

  const filtered = useMemo(() => {
    const dateFiltered = filterByDate(activitiesData, dateFilter, customDate);
    // Group by activity name, sum redemptions
    const grouped = {};
    dateFiltered.forEach((item) => {
      const name = item.name?.trim() || "Unknown";
      if (!grouped[name])
        grouped[name] = { name, totalRedemptions: 0, dates: [] };
      grouped[name].totalRedemptions += item.redemptions || 0;
      if (item.date) grouped[name].dates.push(item.date);
    });
    const result = Object.values(grouped);
    if (searchQuery.trim()) {
      return result.filter((a) =>
        a.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }
    return result.sort((a, b) => b.totalRedemptions - a.totalRedemptions);
  }, [activitiesData, dateFilter, customDate, searchQuery]);

  const totalRedemptions = filtered.reduce(
    (sum, a) => sum + a.totalRedemptions,
    0,
  );

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-10">
      <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
        Activity Analytics
      </h2>

      {/* Filters Row */}
      <div className="flex flex-wrap gap-3 mb-6 items-end">
        {/* Search */}
        <div className="flex-1 min-w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search Activity
          </label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Type activity name..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Date Filter
          </label>
          <select
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              if (e.target.value !== "custom") setCustomDate("");
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="thisWeek">This Week</option>
            <option value="thisMonth">This Month</option>
            <option value="custom">Custom Date</option>
          </select>
        </div>

        {dateFilter === "custom" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Date
            </label>
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {/* Clear */}
        {(dateFilter !== "all" || searchQuery) && (
          <button
            onClick={() => {
              setDateFilter("all");
              setCustomDate("");
              setSearchQuery("");
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{filtered.length}</p>
          <p className="text-sm text-gray-600">Activities</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-600">
            {totalRedemptions}
          </p>
          <p className="text-sm text-gray-600">Total Redemptions</p>
        </div>
      </div>

      {/* Chart */}
      {filtered.length > 0 && (
        <div className="mb-6">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={filtered.slice(0, 10)} margin={{ bottom: 60 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#f0f0f0"
                vertical={false}
              />
              <XAxis
                dataKey="name"
                angle={-35}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => [`${value}`, "Redemptions"]} />
              <Bar
                dataKey="totalRedemptions"
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
                name="Redemptions"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Activity List */}
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {filtered.length > 0 ? (
          filtered.map((activity, index) => {
            const maxR = filtered[0].totalRedemptions;
            const pct = maxR > 0 ? (activity.totalRedemptions / maxR) * 100 : 0;
            return (
              <div
                key={index}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 text-center">
                  <span className="text-sm font-bold text-gray-500">
                    #{index + 1}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-800">
                      {activity.name}
                    </span>
                    <span className="text-sm font-bold text-blue-600">
                      {activity.totalRedemptions} redemptions
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No activities found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Error component
const ErrorMessage = ({ message, onRetry }) => (
  <div className="flex flex-col items-center justify-center h-64 text-center">
    <div className="text-red-500 mb-4">
      <svg
        className="w-16 h-16 mx-auto"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">
      Error Loading Data
    </h3>
    <p className="text-gray-600 mb-4">{message}</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
    >
      Retry
    </button>
  </div>
);

// Customer Insight
const CustomerInsightsDashboard = ({ customerInsightsData }) => {
  const [customerInsights, setCustomerInsights] = useState({});
  const [dateFilter, setDateFilter] = useState("all-time");
  const [customDate, setCustomDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [allTimeSummary, setAllTimeSummary] = useState(null);
  const [fetchedInsights, setFetchedInsights] = useState([]); // ✅ store API data locally

  const COLORS = {
    primary: "#3b82f6",
    secondary: "#10b981",
    indigo: "#6366f1",
    teal: "#14b8a6",
    purple: "#8b5cf6",
    rose: "#f43f5e",
  };

  const PIE_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

  const emptyFallback = {
    popularPackages: [],
    ageGroups: [],
    totalCustomers: 0,
    avgOrderValue: 0,
    repeatRate: 0,
    dailyUniqueVisitors: 0,
    dailyTotalVisitors: 0,
  };

  const getFilteredData = (data, filter, customDateValue, summary = null) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (!data || data.length === 0) return emptyFallback;

    switch (filter) {
      case "all-time": {
        const allPackages = {};
        const allAgeGroups = {};

        data.forEach((item) => {
          if (item.popularPackages) {
            item.popularPackages.forEach((pkg) => {
              allPackages[pkg.name] = (allPackages[pkg.name] || 0) + pkg.sales;
            });
          }
          if (item.ageGroups) {
            item.ageGroups.forEach((group) => {
              allAgeGroups[group.ageRange] =
                (allAgeGroups[group.ageRange] || 0) + group.count;
            });
          }
        });

        // ✅ Correctly calculate all-time repeat rate as average of daily rates
        const validRepeatRates = data.filter(
          (d) => d.repeatRate !== undefined && d.repeatRate !== null,
        );
        const avgRepeatRate =
          validRepeatRates.length > 0
            ? validRepeatRates.reduce((sum, d) => sum + d.repeatRate, 0) /
              validRepeatRates.length
            : 0;

        // ✅ Correctly calculate all-time avg order value as average of daily values
        const validOrderValues = data.filter((d) => d.avgOrderValue > 0);
        const avgOrderValue =
          validOrderValues.length > 0
            ? validOrderValues.reduce((sum, d) => sum + d.avgOrderValue, 0) /
              validOrderValues.length
            : 0;

        // ✅ Total customers = latest document's cumulative count
        const sortedByDate = [...data].sort(
          (a, b) => new Date(b.date) - new Date(a.date),
        );
        const latestTotalCustomers = sortedByDate[0]?.totalCustomers ?? 0;

        return {
          popularPackages: Object.entries(allPackages)
            .map(([name, sales]) => ({ name, sales }))
            .sort((a, b) => b.sales - a.sales),
          ageGroups: Object.entries(allAgeGroups).map(([ageRange, count]) => ({
            ageRange,
            count,
          })),
          totalCustomers: summary?.totalCustomers ?? latestTotalCustomers,
          avgOrderValue: summary?.avgOrderValue ?? avgOrderValue,
          repeatRate: summary?.avgRepeatRate ?? avgRepeatRate,
        };
      }

      case "today": {
        const found = data.find(
          (item) => new Date(item.date).toDateString() === today.toDateString(),
        );
        return found ?? emptyFallback;
      }

      case "yesterday": {
        const found = data.find(
          (item) =>
            new Date(item.date).toDateString() === yesterday.toDateString(),
        );
        return found ?? emptyFallback;
      }

      case "custom": {
        if (!customDateValue) return emptyFallback;
        const found = data.find(
          (item) =>
            new Date(item.date).toDateString() ===
            new Date(customDateValue).toDateString(),
        );
        return found ?? emptyFallback;
      }

      default:
        return emptyFallback;
    }
  };

  // ✅ Single fetch on mount - store insights locally
  useEffect(() => {
    setLoading(true);
    const fetchInsights = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/insights/customerinsightfetch`,
        );
        const { insights, allTimeSummary: summary } = response.data;

        // ✅ Store both locally
        setFetchedInsights(insights || []);
        setAllTimeSummary(summary);

        // ✅ Set initial all-time view
        const filteredData = getFilteredData(
          insights || [],
          "all-time",
          "",
          summary,
        );
        setCustomerInsights(filteredData);
      } catch (err) {
        console.error("Failed to fetch insights", err);
        setCustomerInsights(emptyFallback);
      } finally {
        setLoading(false);
      }
    };
    fetchInsights();
  }, []);

  // ✅ Use fetchedInsights (local) not customerInsightsData (prop) for filtering
  useEffect(() => {
    if (loading || fetchedInsights.length === 0) return;
    setDataLoading(true);
    setTimeout(() => {
      const filteredData = getFilteredData(
        fetchedInsights, // ✅ use local state, not prop
        dateFilter,
        customDate,
        allTimeSummary,
      );
      setCustomerInsights(filteredData || emptyFallback);
      setDataLoading(false);
    }, 300);
  }, [dateFilter, customDate, fetchedInsights]); // ✅ also depend on fetchedInsights

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 relative overflow-hidden">
      <div
        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-lg"
        style={{ backgroundColor: color }}
      ></div>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div
          className="p-3 rounded-full ml-4"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </div>
  );

  const ChartCard = ({ title, children }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 relative">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      {dataLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-xl z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}
      <div className={dataLoading ? "opacity-50" : ""}>{children}</div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading customer insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Customer Insights
          </h2>

          {/* Date Filter */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Filter by Date:
                </span>
              </div>
              <div className="flex gap-2">
                {[
                  { value: "all-time", label: "All Time" },
                  { value: "today", label: "Today" },
                  { value: "yesterday", label: "Yesterday" },
                  { value: "custom", label: "Custom Date" },
                ].map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setDateFilter(f.value)}
                    disabled={dataLoading}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                      dateFilter === f.value
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {dateFilter === "custom" && (
                <input
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  disabled={dataLoading}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50"
                />
              )}
              {dataLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span>Updating data...</span>
                </div>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div
              className={`transition-opacity duration-200 ${dataLoading ? "opacity-50" : ""}`}
            >
              <StatCard
                title="Repeat Customer Rate"
                value={`${((customerInsights.repeatRate || 0) * 100).toFixed(1)}%`}
                icon={UserCheck}
                color={COLORS.secondary}
              />
            </div>
            <div
              className={`transition-opacity duration-200 ${dataLoading ? "opacity-50" : ""}`}
            >
              <StatCard
                title="Avg Customer Order"
                value={formatCurrency(customerInsights.avgOrderValue || 0)}
                icon={Package}
                color={COLORS.indigo}
              />
            </div>
            <div
              className={`transition-opacity duration-200 ${dataLoading ? "opacity-50" : ""}`}
            >
              <StatCard
                title="Total Customers"
                value={(customerInsights.totalCustomers || 0).toLocaleString()}
                icon={Users}
                color={COLORS.teal}
              />
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Popular Packages */}
            <ChartCard title="Most Popular Packages">
              {customerInsights.popularPackages?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={380}>
                    <BarChart
                      data={customerInsights.popularPackages}
                      margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                    >
                      <defs>
                        <linearGradient
                          id="barGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#3b82f6"
                            stopOpacity={0.9}
                          />
                          <stop
                            offset="100%"
                            stopColor="#1d4ed8"
                            stopOpacity={0.7}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        opacity={0.5}
                      />
                      <XAxis
                        dataKey="name"
                        stroke="#6b7280"
                        angle={-35}
                        textAnchor="end"
                        height={80}
                        fontSize={11}
                        fontWeight={500}
                      />
                      <YAxis stroke="#6b7280" fontSize={11} />
                      <Tooltip
                        formatter={(value, name, props) => [
                          `${value} sales`,
                          props.payload.name,
                        ]}
                        contentStyle={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                      <Bar
                        dataKey="sales"
                        fill="url(#barGradient)"
                        radius={[6, 6, 0, 0]}
                        stroke="#1d4ed8"
                        strokeWidth={1}
                      />
                    </BarChart>
                  </ResponsiveContainer>

                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {customerInsights.popularPackages.length}
                        </div>
                        <div className="text-sm text-blue-700 font-medium">
                          Total Packages
                        </div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {customerInsights.popularPackages.reduce(
                            (sum, pkg) => sum + pkg.sales,
                            0,
                          )}
                        </div>
                        <div className="text-sm text-green-700 font-medium">
                          Total Sales
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-700 mb-3">
                        📊 Performance Insights
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Best Performer:</span>
                          <span className="font-semibold text-gray-800">
                            {
                              customerInsights.popularPackages.reduce(
                                (max, pkg) =>
                                  pkg.sales > max.sales ? pkg : max,
                              ).name
                            }
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Top Sales:</span>
                          <span className="font-semibold text-purple-600">
                            {Math.max(
                              ...customerInsights.popularPackages.map(
                                (pkg) => pkg.sales,
                              ),
                            )}{" "}
                            units
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">Average Sales:</span>
                          <span className="font-semibold text-orange-600">
                            {(
                              customerInsights.popularPackages.reduce(
                                (sum, pkg) => sum + pkg.sales,
                                0,
                              ) / customerInsights.popularPackages.length
                            ).toFixed(1)}{" "}
                            units
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border">
                      <h4 className="font-semibold text-gray-700 mb-3">
                        🏆 Top 3 Packages
                      </h4>
                      <div className="space-y-2">
                        {customerInsights.popularPackages
                          .sort((a, b) => b.sales - a.sales)
                          .slice(0, 3)
                          .map((pkg, index) => {
                            const medals = ["🥇", "🥈", "🥉"];
                            const colors = [
                              "text-yellow-600",
                              "text-gray-500",
                              "text-amber-600",
                            ];
                            return (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-white p-2 rounded"
                              >
                                <div className="flex items-center space-x-2">
                                  <span className="text-lg">
                                    {medals[index]}
                                  </span>
                                  <span
                                    className={`font-medium ${colors[index]}`}
                                  >
                                    {pkg.name}
                                  </span>
                                </div>
                                <span className="font-bold text-gray-800">
                                  {pkg.sales} sales
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                  <Package className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-sm font-medium text-gray-500">
                    No package sales for this date
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Try selecting a different date
                  </p>
                </div>
              )}
            </ChartCard>

            {/* Age Distribution */}
            <ChartCard title="Customer Age Distribution">
              {customerInsights.ageGroups?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <defs>
                        {PIE_COLORS.map((color, index) => (
                          <linearGradient
                            key={index}
                            id={`pieGradient${index}`}
                            x1="0"
                            y1="0"
                            x2="1"
                            y2="1"
                          >
                            <stop
                              offset="0%"
                              stopColor={color}
                              stopOpacity={0.9}
                            />
                            <stop
                              offset="100%"
                              stopColor={color}
                              stopOpacity={0.6}
                            />
                          </linearGradient>
                        ))}
                      </defs>
                      <Pie
                        data={customerInsights.ageGroups}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ ageRange, percent }) =>
                          percent > 0.05
                            ? `${ageRange} ${(percent * 100).toFixed(0)}%`
                            : ""
                        }
                        outerRadius={100}
                        innerRadius={40}
                        paddingAngle={2}
                        dataKey="count"
                        stroke="#ffffff"
                        strokeWidth={2}
                      >
                        {customerInsights.ageGroups.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={`url(#pieGradient${index % PIE_COLORS.length})`}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name, props) => [
                          `${value} customers`,
                          props.payload.ageRange,
                        ]}
                        contentStyle={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>

                  <div className="mt-4 space-y-2">
                    {customerInsights.ageGroups.map((group, index) => {
                      const total = customerInsights.ageGroups.reduce(
                        (sum, g) => sum + g.count,
                        0,
                      );
                      const percentage =
                        total > 0
                          ? ((group.count / total) * 100).toFixed(1)
                          : "0.0";
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded"
                        >
                          <div className="flex items-center space-x-3">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{
                                backgroundColor:
                                  PIE_COLORS[index % PIE_COLORS.length],
                              }}
                            ></div>
                            <span className="text-sm font-medium text-gray-700">
                              {group.ageRange}
                            </span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-gray-800">
                              {group.count}
                            </div>
                            <div className="text-xs text-gray-500">
                              {percentage}%
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700 font-medium">
                        Total Customers
                      </span>
                      <span className="text-xl font-bold text-blue-800">
                        {customerInsights.ageGroups.reduce(
                          (sum, group) => sum + group.count,
                          0,
                        )}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                  <Users className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-sm font-medium text-gray-500">
                    No age data for this date
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Try selecting a different date
                  </p>
                </div>
              )}
            </ChartCard>
          </div>

          {/* Additional Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {customerInsights.ageGroups?.length > 0 && (
              <ChartCard title="Age Groups Detailed View">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={customerInsights.ageGroups}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="ageRange" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip formatter={(value) => [value, "Customers"]} />
                    <Bar
                      dataKey="count"
                      fill={COLORS.secondary}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {customerInsights.popularPackages?.length > 0 && (
              <ChartCard title="Package Sales Distribution">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={customerInsights.popularPackages}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="sales"
                    >
                      {customerInsights.popularPackages.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={PIE_COLORS[index % PIE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-3 border rounded shadow-lg">
                              <p className="font-semibold text-gray-800">
                                {data.name}
                              </p>
                              <p className="text-blue-600">
                                Sales: {data.sales}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Stats = () => {
  const [salesData, setSalesData] = useState([]);
  const [transactionData, setTransactionData] = useState([]);
  const [activitiesData, setActivitiesData] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [manualSalesData, setManualSalesData] = useState([]);
  const [priceData, setPriceData] = useState([]);
  const [customerInsights, setCustomerInsights] = useState([]);
  const { token } = useSelector((store) => store.userSlice);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("All");
  const [activeSplit, setActiveSplit] = useState("All");

  // Date filtering states
  const [dateFilterType, setDateFilterType] = useState("all");
  const [dateFilterValue, setDateFilterValue] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);

  const handleExcel = async () => {
    try {
      const response = await axios.get(`${apiUrl}/bill/billinexcel`, {
        responseType: "blob",
        headers: {
          token,
        },
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "FUNINGO_BILLS.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      alert("Failed to download Excel file. Please try again.");
    }
  };

  const processData = async () => {
    try {
      const response = await axios.get(`${apiUrl}/bill/adjustedbillinexcel`, {
        responseType: "blob",
        headers: {
          token,
        },
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "FUNINGO_Processed_BILLS.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      alert("Failed to download Excel file. Please try again.");
    }
  };

  // Fetch data from API with optional date filters
  const fetchData = async (dateFilter = null) => {
    try {
      setLoading(true);
      setError(null);

      // API para for date filtering
      const params = {};
      if (dateFilter) {
        params.dateFilter = dateFilter;
      }

      const [
        salesResponse,
        activitiesResponse,
        insightsResponse,
        employeeResponse,
        transactionResponse,
        priceingTransactionRes,
        manualSalesEntryRes,
      ] = await Promise.all([
        axios.get(`${apiUrl}/insights/salesfetch`, { params }),
        axios.get(`${apiUrl}/insights/activityfetch`, { params }),
        axios.get(`${apiUrl}/insights/customerinsightfetch`, { params }),
        axios.get(`${apiUrl}/insights/employeefetch`, { params }),
        axios.get(`${apiUrl}/bill/billfetch`, { params }),
        axios.get(`${apiUrl}/user/getalltransactions`, {
          headers: {
            token,
          },
        }),
        axios.get(`${apiUrl}/manualsales/sales`, {
          headers: {
            token,
          },
        }),
      ]);

      // Seted data with proper validation
      const salesDataArray = Array.isArray(salesResponse.data)
        ? salesResponse.data
        : [];
      const transactionDataArray = Array.isArray(transactionResponse.data.data)
        ? transactionResponse.data.data
        : [];
      const activitiesDataArray = Array.isArray(activitiesResponse.data)
        ? activitiesResponse.data
        : [];
      const employeeDataArray = Array.isArray(employeeResponse.data)
        ? employeeResponse.data
        : [];
      const priceDataArray = Array.isArray(priceingTransactionRes.data.data)
        ? priceingTransactionRes.data.data
        : [];
      const customerInsightArray = Array.isArray(insightsResponse.data)
        ? insightsResponse.data
        : [];
      const manualEntriesArray = Array.isArray(
        manualSalesEntryRes.data.data.entries,
      )
        ? manualSalesEntryRes.data.data.entries
        : [];
      setSalesData(salesDataArray);
      setTransactionData(transactionDataArray);
      setCustomerInsights(customerInsightArray);
      setActivitiesData(activitiesDataArray);
      setEmployeeData(employeeDataArray);
      setPriceData(priceDataArray);
      setManualSalesData(manualEntriesArray);
      setAvailableYears(getUniqueYears(salesDataArray));
      setAvailableMonths(getUniqueMonths(salesDataArray));
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch data from server",
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle date filter changes
  const handleDateFilterChange = () => {
    if (dateFilterType === "all") {
      fetchData();
    } else {
      const filterObj = {
        type: dateFilterType,
        value: dateFilterValue,
      };
      fetchData(filterObj);
    }
  };
  // Clear date filters
  const clearDateFilter = () => {
    setDateFilterType("all");
    setDateFilterValue("");
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Apply date filter whenever filter values change
  useEffect(() => {
    if (dateFilterValue || dateFilterType === "all") {
      handleDateFilterChange();
    }
  }, [dateFilterType, dateFilterValue]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <ErrorMessage message={error} onRetry={fetchData} />
      </div>
    );
  }

  // Apply client-side date filtering for immediate UI updates
  const dateFilteredSalesData = filterDataByDate(
    salesData,
    dateFilterType,
    dateFilterValue,
  );
  const dateFilteredActivitiesData = filterDataByDate(
    activitiesData,
    dateFilterType,
    dateFilterValue,
  );

  // Filter data based on section and split selections
  const filteredSalesData = dateFilteredSalesData.filter((item) => {
    const sectionMatch =
      activeSection === "All" || item.section === activeSection;
    const splitMatch = activeSplit === "All" || item.split === activeSplit;
    return sectionMatch && splitMatch;
  });

  // Calculate metrics
  const totalRevenue = filteredSalesData.reduce(
    (sum, item) => sum + (item.amount || 0),
    0,
  );
  const totalOrders = filteredSalesData.length;
  const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  // Get unique sections and splits from filtered data
  const availableSections = [
    "All",
    ...new Set(
      dateFilteredSalesData.map((item) => item.section).filter(Boolean),
    ),
  ];
  const availableSplits = [
    "All",
    ...new Set(dateFilteredSalesData.map((item) => item.split).filter(Boolean)),
  ];
  // Prepare chart data
  const dailySalesData = filteredSalesData
    .reduce((acc, item) => {
      const date = formatDate(item.date);
      const existing = acc.find((d) => d.date === date);
      if (existing) {
        existing.amount += item.amount || 0;
        existing.orders += 1;
      } else {
        acc.push({ date, amount: item.amount || 0, orders: 1 });
      }
      return acc;
    }, [])
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  // Section-wise revenue
  const sectionData = filteredSalesData.reduce((acc, item) => {
    if (!item.section) return acc;
    const existing = acc.find((d) => d.name === item.section);
    if (existing) {
      existing.value += item.amount || 0;
    } else {
      acc.push({ name: item.section, value: item.amount || 0 });
    }
    return acc;
  }, []);

  // Split-wise revenue
  const splitData = filteredSalesData.reduce((acc, item) => {
    if (!item.split) return acc;
    const existing = acc.find((d) => d.name === item.split);
    if (existing) {
      existing.value += item.amount || 0;
    } else {
      acc.push({ name: item.split, value: item.amount || 0 });
    }
    return acc;
  }, []);

  // Top activities by redemptions (with date filtering)
  const topActivities = [...dateFilteredActivitiesData]
    .sort((a, b) => (b.redemptions || 0) - (a.redemptions || 0))
    .slice(0, 5)
    .map((activity) => ({
      name: activity.name || "Unknown Activity",
      redemptions: activity.redemptions || 0,
      staff: Array.isArray(activity.assignedPeople)
        ? activity.assignedPeople.length
        : 0,
    }));

  // Calculate total customers from age groupsx

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* Header Content */}
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <div className="flex items-center space-x-2">
                    <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                      Dashboard
                    </h1>
                  </div>
                </div>
                <p className="text-gray-600 text-base lg:text-lg">
                  Monitor your business performance and key metrics
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  onClick={fetchData}
                  className="group px-6 py-3 bg-white text-gray-700 rounded-xl hover:bg-gray-50 transition-all duration-200 flex items-center justify-center gap-3 shadow-md hover:shadow-lg border border-gray-200 hover:border-gray-300 font-medium"
                >
                  <Calendar className="w-5 h-5 text-blue-600 group-hover:rotate-12 transition-transform duration-200" />
                  <span>Refresh Data</span>
                  <div className="hidden lg:block w-2 h-2 bg-blue-500 rounded-full group-hover:animate-pulse"></div>
                </button>

                <button
                  onClick={processData}
                  className="group px-6 py-3 bg-gradient-to-r from-red-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
                >
                  <Download className="w-5 h-5 group-hover:animate-bounce" />
                  <span>Processed Sales</span>
                  <div className="hidden lg:flex items-center justify-center w-6 h-6 bg-white/20 rounded-full">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19 9h-4V3H9v6H5l7 7 7-7z" />
                    </svg>
                  </div>
                </button>

                <button
                  onClick={handleExcel}
                  className="group px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
                >
                  <Download className="w-5 h-5 group-hover:animate-bounce" />
                  <span>Export Sales</span>
                  <div className="hidden lg:flex items-center justify-center w-6 h-6 bg-white/20 rounded-full">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M19 9h-4V3H9v6H5l7 7 7-7z" />
                    </svg>
                  </div>
                </button>
              </div>
            </div>

            {/* Status Bar - Only visible on larger screens */}
            <div className="hidden lg:flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">System Online</span>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Section and Split Filters */}
        {dateFilteredSalesData.length > 0 && (
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section
                </label>
                <FilterTabs
                  tabs={availableSections}
                  activeTab={activeSection}
                  onTabChange={setActiveSection}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <FilterTabs
                  tabs={availableSplits}
                  activeTab={activeSplit}
                  onTabChange={setActiveSplit}
                />
              </div>
            </div>
          </div>
        )}

        {/* Today Performance */}
        <div className="mb-10">
          <TodaysStatsCard salesData={salesData} />
        </div>

        {/* Manual Entry */}

        <div className="mb-10">
          <ManualSalesEntryCard salesData={manualSalesData} />
        </div>

        {/* Discount Performance */}
        <div className="mb-10">
          <DiscountTaxStatsCard transactionData={transactionData} />
        </div>

        {/* Payment Performance */}
        <div className="mb-10">
          <PaymentAnalyticsCard salesData={transactionData} />
        </div>

        {/* Sales  Performance*/}
        <div className="mb-10">
          <SalesDashboard data={transactionData} />
        </div>

        {/* extra sales features  */}

        {filteredSalesData.length > 0 &&
          (() => {
            // Process hourly data
            const hourlyStats = {};
            filteredSalesData.forEach((sale) => {
              const hour = new Date(sale.date).getHours();
              if (!hourlyStats[hour]) {
                hourlyStats[hour] = 0;
              }
              hourlyStats[hour]++;
            });

            const hourlyData = Array.from({ length: 24 }, (_, hour) => ({
              hour,
              transactions: hourlyStats[hour] || 0,
            }));

            // Process section comparison data
            const sectionStats = {};
            filteredSalesData.forEach((sale) => {
              if (!sectionStats[sale.section]) {
                sectionStats[sale.section] = {
                  revenue: 0,
                  transactions: 0,
                };
              }
              sectionStats[sale.section].revenue += sale.amount;
              sectionStats[sale.section].transactions++;
            });

            const sectionComparisonData = Object.keys(sectionStats).map(
              (section) => ({
                section,
                revenue: sectionStats[section].revenue,
                transactions: sectionStats[section].transactions,
                avgTransaction:
                  sectionStats[section].revenue /
                  sectionStats[section].transactions,
              }),
            );

            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                {/* Daily Sales Trend with Volume */}
                <ChartCard
                  title="Daily Sales & Transaction Volume"
                  className="lg:col-span-2 xl:col-span-3"
                >
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={dailySalesData}>
                      <defs>
                        <linearGradient
                          id="colorRevenue"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#3b82f6"
                            stopOpacity={0.4}
                          />
                          <stop
                            offset="95%"
                            stopColor="#3b82f6"
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                        <linearGradient
                          id="colorVolume"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#10b981"
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="95%"
                            stopColor="#10b981"
                            stopOpacity={0.05}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#f1f5f9"
                        opacity={0.7}
                      />
                      <XAxis
                        dataKey="date"
                        stroke="#64748b"
                        fontSize={12}
                        tickFormatter={(value) =>
                          new Date(value).toLocaleDateString("en-IN", {
                            month: "short",
                            day: "numeric",
                          })
                        }
                      />
                      <YAxis
                        yAxisId="revenue"
                        stroke="#3b82f6"
                        tickFormatter={(value) =>
                          `₹${(value / 1000).toFixed(0)}k`
                        }
                        fontSize={12}
                      />
                      <YAxis
                        yAxisId="volume"
                        orientation="right"
                        stroke="#10b981"
                        tickFormatter={(value) => `${value}`}
                        fontSize={12}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          name === "amount"
                            ? `₹${value.toLocaleString("en-IN")}`
                            : `${value} transactions`,
                          name === "amount" ? "Revenue" : "Transaction Amount",
                        ]}
                        labelFormatter={(value) =>
                          new Date(value).toLocaleDateString("en-IN", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        }
                        contentStyle={{
                          backgroundColor: "#ffffff",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                      <Legend
                        wrapperStyle={{ paddingTop: "20px" }}
                        iconType="rect"
                      />
                      <Area
                        yAxisId="revenue"
                        type="monotone"
                        dataKey="amount"
                        stroke="#3b82f6"
                        strokeWidth={3}
                        fill="url(#colorRevenue)"
                        name="Revenue"
                        dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                        activeDot={{
                          r: 6,
                          stroke: "#3b82f6",
                          strokeWidth: 2,
                          fill: "#ffffff",
                        }}
                      />
                      <Bar
                        yAxisId="volume"
                        dataKey="orders"
                        fill="url(#colorVolume)"
                        name="Transaction Count"
                        radius={[2, 2, 0, 0]}
                        opacity={0.8}
                      />
                      <Line
                        yAxisId="revenue"
                        type="monotone"
                        dataKey="avgTransaction"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Avg Transaction"
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* Enhanced Section-wise Revenue */}
                {sectionData.length > 0 && (
                  <ChartCard
                    title="Revenue Distribution by Section"
                    className="xl:col-span-2"
                  >
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <defs>
                          {PIE_COLORS.map((color, index) => (
                            <linearGradient
                              key={`gradient-${index}`}
                              id={`pieGradient${index}`}
                              x1="0"
                              y1="0"
                              x2="1"
                              y2="1"
                            >
                              <stop
                                offset="0%"
                                stopColor={color}
                                stopOpacity={1}
                              />
                              <stop
                                offset="100%"
                                stopColor={color}
                                stopOpacity={0.7}
                              />
                            </linearGradient>
                          ))}
                        </defs>
                        <Pie
                          data={sectionData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent, value }) =>
                            percent > 5
                              ? `${name}\n${(percent * 100).toFixed(1)}%\n₹${(
                                  value / 1000
                                ).toFixed(0)}k`
                              : ""
                          }
                          outerRadius={120}
                          innerRadius={40}
                          fill="#8884d8"
                          dataKey="value"
                          paddingAngle={2}
                        >
                          {sectionData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={`url(#pieGradient${
                                index % PIE_COLORS.length
                              })`}
                              stroke="#ffffff"
                              strokeWidth={2}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name) => [
                            `₹${value.toLocaleString("en-IN")}`,
                            "Revenue",
                          ]}
                          contentStyle={{
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                          }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          formatter={(value) => `${value}`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {/* Enhanced Category Performance */}
                {splitData.length > 0 && (
                  <ChartCard
                    title="Category Performance"
                    className="xl:col-span-1"
                  >
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart
                        data={splitData}
                        layout="horizontal"
                        margin={{ left: 80 }}
                      >
                        <defs>
                          <linearGradient
                            id="barGradient"
                            x1="0"
                            y1="0"
                            x2="1"
                            y2="0"
                          >
                            <stop
                              offset="0%"
                              stopColor="#8b5cf6"
                              stopOpacity={0.8}
                            />
                            <stop
                              offset="100%"
                              stopColor="#a78bfa"
                              stopOpacity={1}
                            />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis
                          type="number"
                          tickFormatter={(value) =>
                            `₹${(value / 1000).toFixed(0)}k`
                          }
                          stroke="#64748b"
                          fontSize={11}
                        />
                        <YAxis
                          dataKey="name"
                          type="category"
                          stroke="#64748b"
                          fontSize={11}
                          width={75}
                        />
                        <Tooltip
                          formatter={(value) => [
                            `₹${value.toLocaleString("en-IN")}`,
                            "Revenue",
                          ]}
                          contentStyle={{
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                          }}
                        />
                        <Bar
                          dataKey="value"
                          fill="url(#barGradient)"
                          radius={[0, 6, 6, 0]}
                          stroke="#7c3aed"
                          strokeWidth={1}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}

                {/* Section Comparison Radar */}
                {sectionComparisonData.length > 1 && (
                  <ChartCard
                    title="Section Performance Metrics"
                    className="xl:col-span-1"
                  >
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={sectionComparisonData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis
                          dataKey="section"
                          stroke="#64748b"
                          fontSize={10}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis stroke="#64748b" fontSize={11} />
                        <Tooltip
                          formatter={(value, name) => [
                            name === "revenue"
                              ? `₹${value.toLocaleString("en-IN")}`
                              : name === "avgTransaction"
                                ? `₹${value.toLocaleString("en-IN")}`
                                : value,
                            name === "revenue"
                              ? "Total Revenue"
                              : name === "avgTransaction"
                                ? "Avg Transaction"
                                : "Transaction Count",
                          ]}
                          contentStyle={{
                            backgroundColor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                          }}
                        />
                        <Bar
                          dataKey="transactions"
                          fill="#fbbf24"
                          name="Transaction Count"
                          radius={[2, 2, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}
              </div>
            );
          })()}
        {/* Activity Analytics */}
        <ActivityAnalyticsCard activitiesData={activitiesData} />

        {/* Coin Performance */}
        <div className="mb-10">
          <AdminCoinAnalytics priceData={priceData} />
        </div>

        {/* Employee Performance */}
        {employeeData.length > 0 && (
          <EmployeePerformanceCard employeeData={employeeData} />
        )}

        {/* Customer Insights Section */}
        <CustomerInsightsDashboard customerInsightsData={customerInsights} />

        {/* Detailed Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Transactions */}
          {filteredSalesData.length > 0 && (
            <ChartCard title="Recent Transactions">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Section
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {[...filteredSalesData]
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .slice(0, 10)
                      .map((item) => (
                        <tr key={item._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formatDate(item.date)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {item.section || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-green-600">
                            {formatCurrency(item.amount || 0)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          )}

          {/* Top Activities Details */}
          {topActivities.length > 0 && (
            <ChartCard title="Activity Details">
              <div className="space-y-4">
                {topActivities.slice(0, 5).map((activity, index) => (
                  <div
                    key={activity.name}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {activity.name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {activity.staff} staff assigned
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-blue-600">
                        {activity.redemptions}
                      </p>
                      <p className="text-xs text-gray-500">redemptions</p>
                    </div>
                  </div>
                ))}
              </div>
            </ChartCard>
          )}

          {/* Customer Package Preferences */}

          {customerInsights.length > 0 && (
            <ChartCard
              title="Package Performance"
              subtitle="Top 3 packages for the last 3 days"
            >
              <div className="space-y-6">
                {(() => {
                  // Sort insights by date (newest first) and take last 3 days
                  const sortedInsights = customerInsights
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .slice(0, 3);

                  const dayLabels = [
                    "Today",
                    "Yesterday",
                    "Day Before Yesterday",
                  ];

                  return sortedInsights.map((insight, dayIndex) => {
                    // Get top 3 packages for this day
                    const topPackages = insight.popularPackages
                      .sort((a, b) => (b.sales || 0) - (a.sales || 0))
                      .slice(0, 3);

                    // Find max sales across all days for consistent scaling
                    const allPackages = customerInsights.flatMap(
                      (ci) => ci.popularPackages || [],
                    );
                    const maxSales = Math.max(
                      ...allPackages.map((p) => p.sales || 0),
                    );

                    const date = new Date(insight.date);
                    const formattedDate = date.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    });

                    return (
                      <div
                        key={insight._id}
                        className="border rounded-lg p-4 bg-gradient-to-r from-gray-50 to-blue-50"
                      >
                        {/* Day Header */}
                        <div className="flex justify-between items-center mb-4">
                          <div>
                            <h4 className="font-semibold text-gray-800">
                              {dayLabels[dayIndex] ||
                                `${dayIndex + 1} days ago`}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {formattedDate}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-600">
                              Total Sales
                            </div>
                            <div className="text-lg font-bold text-blue-600">
                              {(insight.popularPackages || []).reduce(
                                (sum, pkg) => sum + (pkg.sales || 0),
                                0,
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Top 3 Packages */}
                        <div className="space-y-3">
                          {topPackages.length > 0 ? (
                            topPackages.map((pkg, index) => {
                              const percentage =
                                maxSales > 0
                                  ? ((pkg.sales || 0) / maxSales) * 100
                                  : 0;
                              const medals = ["🥇", "🥈", "🥉"];
                              const colors = [
                                "bg-gradient-to-r from-yellow-400 to-yellow-500",
                                "bg-gradient-to-r from-gray-400 to-gray-500",
                                "bg-gradient-to-r from-amber-400 to-amber-500",
                              ];

                              return (
                                <div
                                  key={pkg._id || index}
                                  className="space-y-2"
                                >
                                  <div className="flex justify-between items-center">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-lg">
                                        {medals[index]}
                                      </span>
                                      <span className="text-sm font-medium text-gray-700 truncate">
                                        {pkg.name || "Unknown Package"}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm font-semibold text-blue-600">
                                        {pkg.sales || 0} sales
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        ({percentage.toFixed(0)}%)
                                      </span>
                                    </div>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                      className={`${colors[index]} h-2.5 rounded-full transition-all duration-700 shadow-sm`}
                                      style={{ width: `${percentage}%` }}
                                    ></div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="text-center py-4 text-gray-500">
                              <p className="text-sm">
                                No package sales recorded for this day
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Day Stats */}
                        {topPackages.length > 0 && (
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <div className="flex justify-between text-xs text-gray-600">
                              <span>
                                Packages Active:{" "}
                                {(insight.popularPackages || []).length}
                              </span>
                              <span>
                                Best: {topPackages[0]?.name} (
                                {topPackages[0]?.sales} sales)
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}

                {/* Summary Stats */}
                {customerInsights.length > 0 && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border">
                    <h4 className="font-semibold text-gray-700 mb-3">
                      📈 3-Day Summary
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {(() => {
                            const last3Days = customerInsights.slice(0, 3);
                            return last3Days.reduce(
                              (total, insight) =>
                                total +
                                (insight.popularPackages || []).reduce(
                                  (sum, pkg) => sum + (pkg.sales || 0),
                                  0,
                                ),
                              0,
                            );
                          })()}
                        </div>
                        <div className="text-sm text-gray-600">Total Sales</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {(() => {
                            const last3Days = customerInsights.slice(0, 3);
                            const allPackages = last3Days.flatMap(
                              (insight) => insight.popularPackages || [],
                            );
                            const uniquePackages = [
                              ...new Set(allPackages.map((pkg) => pkg.name)),
                            ];
                            return uniquePackages.length;
                          })()}
                        </div>
                        <div className="text-sm text-gray-600">
                          Unique Packages
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ChartCard>
          )}
        </div>

        {/* No Data Message */}
        {salesData.length === 0 &&
          activitiesData.length === 0 &&
          customerInsights.popularPackages.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Data Available
              </h3>
              <p className="text-gray-600">
                There's no data to display at the moment. Please check back
                later or refresh the page.
              </p>
            </div>
          )}
      </div>
    </div>
  );
};

export default Stats;
// develop by gagan shukla
// github GaganShukla01
